import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

export const FUTURE_PRESETS = [
  { value: 'next_7', label: 'Next 7 days', days: 7 },
  { value: 'next_14', label: 'Next 14 days', days: 14 },
  { value: 'next_30', label: 'Next 30 days', days: 30 },
  { value: 'custom', label: 'Custom' },
];

export const PIPELINE_FILTERS = [
  ['all', 'All upcoming'], ['within_3_days', 'Starts within 3 days'],
  ['within_7_days', 'Starts within 7 days'], ['zero_players', 'Zero players'],
  ['under_10_percent', 'Under 10% full'], ['full', 'Full'],
  ['with_waitlist', 'With waitlist'], ['training', 'Training'], ['run', 'Run'],
];

export const PIPELINE_SORTS = [
  ['priority', 'Factual priority'], ['soonest', 'Soonest first'], ['latest', 'Latest first'],
  ['lowest_fill', 'Lowest fill first'], ['highest_fill', 'Highest fill first'],
  ['most_joined', 'Most joined'], ['most_waitlisted', 'Most waitlisted'],
];

export const CLUB_PIPELINE_FILTERS = [
  ['all', 'All clubs'], ['has_upcoming', 'Has upcoming sessions'],
  ['no_upcoming', 'No upcoming sessions'], ['zero_players', 'Zero-player sessions'],
  ['with_waitlist', 'With waitlist'],
];

export const getAdminHostReportMode = (searchParams) => (
  searchParams.get('mode') === 'history' ? 'history' : 'upcoming'
);

export const getFuturePresetDates = (preset, viewerTimezone, now = dayjs()) => {
  const today = now.tz(viewerTimezone).startOf('day');
  const selected = FUTURE_PRESETS.find((item) => item.value === preset);
  const days = selected?.days || 30;
  return {
    start: today.format('YYYY-MM-DD'),
    end: today.add(days - 1, 'day').format('YYYY-MM-DD'),
  };
};

export const validateFutureRange = (start, end, viewerTimezone, now = dayjs()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start || '') || !/^\d{4}-\d{2}-\d{2}$/.test(end || '')) {
    return 'Enter valid start and end dates.';
  }
  const parsedStart = dayjs.utc(start);
  const parsedEnd = dayjs.utc(end);
  if (parsedStart.format('YYYY-MM-DD') !== start || parsedEnd.format('YYYY-MM-DD') !== end) {
    return 'Enter valid start and end dates.';
  }
  const today = now.tz(viewerTimezone).format('YYYY-MM-DD');
  if (start < today) return 'Future range must start today or later.';
  if (start > end) return 'Start date must be on or before end date.';
  if (parsedEnd.diff(parsedStart, 'day') + 1 > 90) return 'Future range cannot exceed 90 days.';
  return null;
};

export const operationalQuery = ({
  preset, start, end, timezone, sessionFilter, clubId, clubFilter, hostRole,
  sort, includeCancelled, page, limit = 25,
}) => ({
  preset,
  start: preset === 'custom' ? start : undefined,
  end: preset === 'custom' ? end : undefined,
  viewer_timezone: timezone,
  session_filter: sessionFilter || 'all',
  club_id: clubId && clubId !== 'all' ? clubId : undefined,
  club_filter: clubFilter || 'all',
  host_role: hostRole || 'all',
  sort: sort || 'priority',
  include_cancelled: Boolean(includeCancelled),
  page,
  limit,
});

export const formatStartsIn = (seconds) => {
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  if (safeSeconds < 3600) return `${Math.max(1, Math.ceil(safeSeconds / 60))} minutes`;
  if (safeSeconds < 86400) return `${Math.ceil(safeSeconds / 3600)} hours`;
  return `${Math.ceil(safeSeconds / 86400)} days`;
};

export const pipelineObservations = (data) => {
  const value = data || {};
  return [
    value.zero_player_within_3_days > 0 && `${value.zero_player_within_3_days} sessions start within 3 days and have zero recorded players.`,
    value.under_ten_percent_within_7_days > 0 && `${value.under_ten_percent_within_7_days} of ${value.sessions_within_7_days} sessions starting within 7 days are under 10% full.`,
    value.clubs_with_no_upcoming_sessions > 0 && `${value.clubs_with_no_upcoming_sessions} clubs have no upcoming sessions in this reporting horizon.`,
    value.sessions_with_waitlist > 0 && `${value.sessions_with_waitlist} upcoming sessions currently have waitlist entries.`,
    value.full_sessions > 0 && `${value.full_sessions} upcoming sessions are already full.`,
  ].filter(Boolean);
};
